import { customElement, property } from 'lit/decorators.js';
import { checkboxStyles } from '@/components/checkbox/checkbox.styles';
import {FormValidationMixin} from "@/components/mixins/form-validation-mixin";
import {html, LitElement, nothing} from "lit";
import { when } from 'lit/directives/when.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {FormControlDependencyMixin} from "@/components/form-control-dependency/form-control-dependency-mixin";
import { CheckedValidator, Validator } from '../validators/validators';

@customElement('sbk-checkbox')
export class SbkCheckbox extends FormControlDependencyMixin(FormValidationMixin(LitElement)) {

    static formAssociated = true;

    static shadowRootOptions = {...LitElement.shadowRootOptions, delegatesFocus: true};

    _internals: ElementInternals;

    @property({type: Boolean, reflect: true})
    checked: boolean = false

    @property()
    description: string = ''

    @property({type: Boolean})
    disabled: boolean = false

    @property()
    label: string = ''

    @property({reflect: true})
    name: string = ''

    @property({type: Boolean})
    required: boolean = false

    @property({type: Boolean})
    multiple: boolean = false

    @property()
    value: string = ''

    validatorMapper: {
        [key: string]: Validator
    } = {
        'required': CheckedValidator,
    }

    constructor() {
        super();
        this._internals = this.attachInternals();
        this.addEventListener('form-control-dependency-show', () => {
            this.show();
            this.setValue();
        });
        this.addEventListener('form-control-dependency-hide', () => {
            this.hide();
            this.resetValue();
        });
    }

    connectedCallback() {
        super.connectedCallback();
        if (this.checked) {
            this._internals.setFormValue(this.value, 'checked');
        } else {
            this._internals.setFormValue(null, null);
        }
    }

    static get styles() {
        return [
            checkboxStyles
        ];
    }

    render() {
        let ariaDescribedby = '';
        if (!this.multiple && (this.description || this.invalid)) {
            ariaDescribedby = `${this.name}-annotation`;
        }
        return html`
            <div part="checkbox-wrapper" class="checkbox__wrapper${this.invalid ? ' checkbox__wrapper--error' : ''}">
                <input id="checkbox" 
                       class="checkbox sr-only"
                       aria-describedby=${ariaDescribedby || nothing}
                       name=${this.name}
                       .value=${this.value}
                       ?checked=${this.checked}
                       ?disabled=${this.disabled}
                       ?required=${this.required}
                       type="checkbox"
                       @change=${this._onChange}
                />
                <label for="checkbox"  class="checkbox__label" part="label">
                    <span class="checkbox__icon" part="icon-wrapper">
                        <sbk-icon part="icon" symbol="checkmark"></sbk-icon>
                    </span>
                    ${unsafeHTML(this.label)}
                    ${when(this.required && !this.multiple, () => html`<i aria-hidden="true" class="label__required-icon">*</i>`)}
                </label>
                ${when(!this.multiple && this.description && !this.invalid,
                        () => html`
                            <sbk-annotation id="${this.name}-annotation" variant="info" ?disabled=${this.disabled}>${unsafeHTML(this.description)}</sbk-annotation>
                        `)}
                ${!this.multiple && this.invalid ? this.validationErrorTemplate(`${this.name}-annotation`) : ""}
            </div>
        `;
    }

    async _onChange(event: Event) {
        const element = <HTMLInputElement>event.target;
        if (element.checked) {
            this._internals.setFormValue(this.value, 'checked');
            this.checked = true;
        } else {
            this._internals.setFormValue(null, null);
            this.checked = false;
        }
        this._checkValidity();
        await this.updateComplete;
        const inputChangeEvent = new CustomEvent('checkbox-change', {
            detail: { value: element.value },
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(inputChangeEvent);
    }

    _checkValidity() {
        if (!this.multiple) {
            const validators = this.getValidators();
            this.handleErrors(validators, '');
        }
    }

    hide(): void {
        this.hidden = true;
    }

    show(): void {
        this.hidden = false;
    }

    resetValue(): void {
        this._internals.setFormValue(null, null);
    }

    setValue(): void {
        if (this.checked) {
            this._internals.setFormValue(this.value, 'checked');
        }
    }
}
